/**
 * Dual Color Variant Swatches for Shopify
 * Handles products with "Top Color" and "Bottom Color" variant options
 * Reads swatch images from variant metafield: custom.color_swatch
 */

class DualColorVariantSwatches {
  constructor(options = {}) { 
    this.options = {
      // Selectors
      variantPickerSelector: options.variantPickerSelector || 'variant-selects, variant-radios',
      productFormSelector: options.productFormSelector || 'product-form',
      
      // Color option names (case-insensitive matching)
      topColorOption: options.topColorOption || 'Top Color',
      bottomColorOption: options.bottomColorOption || 'Bottom Color',
      
      // CDN configuration for product images
    useCdnForProductImages: options.useCdnForProductImages !== false,
    cdnBaseUrl: options.cdnBaseUrl || 'https://pub-f2b3fd86c6d346e48250e4685a980361.r2.dev',
    productImagePrefix: options.productImagePrefix || 'Comfo-Back-Folding-Adirondack',
      
    // Metafield configuration
      metafieldNamespace: options.metafieldNamespace || 'custom',
      metafieldKey: options.metafieldKey || 'color_swatch',
      
      // Swatch styling
      swatchSize: options.swatchSize || '48px',
      enableTooltip: options.enableTooltip !== false,
      
      // Image switching
      enableImageSwitching: options.enableImageSwitching !== false,
      
      ...options
    };
    
    this.productData = null;
    this.currentVariant = null;
    this.selectedOptions = {};
    
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Load product data
    this.loadProductData();
    
    // Wait for variant picker to be ready
    this.waitForVariantPicker(() => {
      this.convertToSwatches();
      this.setupEventListeners();
    });
  }

  waitForVariantPicker(callback, attempts = 0) {
    const picker = document.querySelector(this.options.variantPickerSelector);
    
    if (picker || attempts > 20) {
      callback();
    } else {
      setTimeout(() => this.waitForVariantPicker(callback, attempts + 1), 100);
    }
  }

  loadProductData() {
    // Try multiple methods to get product data
    
    // Method 1: Script tag with product JSON
    const productJsonScript = document.querySelector('[data-product-json]');
    if (productJsonScript) {
      try {
        this.productData = JSON.parse(productJsonScript.textContent);
        console.log('✓ Loaded product data from script tag');
        return;
      } catch (e) {
        console.warn('Could not parse product JSON from script tag');
      }
    }
    
    // Method 2: Look for product data in window
    if (window.product) {
      this.productData = window.product;
      console.log('✓ Loaded product data from window.product');
      return;
    }
    
    // Method 3: Check for section data
    const productSection = document.querySelector('[data-section-type="product"]');
    if (productSection) {
      const sectionId = productSection.dataset.sectionId;
      if (window.Shopify?.sections?.[sectionId]) {
        this.productData = window.Shopify.sections[sectionId].product;
        console.log('✓ Loaded product data from section');
        return;
      }
    }
    
    console.warn('⚠ Could not load product data - some features may not work');
  }

convertToSwatches() {
  // Find the variant picker component
  const variantPicker = document.querySelector(this.options.variantPickerSelector);
  if (!variantPicker) {
    console.warn('Variant picker not found');
    return;
  }

  // Find all fieldsets (one for each option like Size, Top Color, Bottom Color)
  const fieldsets = variantPicker.querySelectorAll('fieldset');
  
  fieldsets.forEach(fieldset => {
    const legend = fieldset.querySelector('legend');
    if (!legend) return;
    
    const optionName = legend.textContent.trim();
    
    // Check if this is one of our color options
    if (this.isColorOption(optionName)) {
      this.convertFieldsetToSwatches(fieldset, optionName);
    }
  });
}

isColorOption(optionName) {
  const normalized = optionName.toLowerCase().trim();
  return normalized === this.options.topColorOption.toLowerCase() ||
         normalized === this.options.bottomColorOption.toLowerCase();
}

convertFieldsetToSwatches(fieldset, optionName) {
  
  // Find all input labels in this fieldset
  const labels = fieldset.querySelectorAll('label');
  
  labels.forEach(label => {
    if (label.classList.contains('swatch-converted')) return;
    
    // Get the input (radio button)
    const input = label.querySelector('input[type="radio"]') || 
                 document.getElementById(label.getAttribute('for'));
    
    if (!input) return;
    
    const colorValue = input.value;
    
    // Get swatch image from metafield
    const swatchImage = this.getSwatchImageForColor(optionName, colorValue);
    //console.log('💡 Received swatch image for', colorValue, ':', swatchImage);

    
    // Create swatch element
    const swatch = this.createSwatchElement(colorValue, swatchImage, optionName);
    
    // Replace label content with swatch
    this.replaceWithSwatch(label, swatch, input);

    // Prevent scrolling when radio button is focused
input.addEventListener('focus', (e) => {
  e.preventDefault();
  window.scrollTo(window.scrollX, window.scrollY);
});

    label.classList.add('swatch-converted');
    
    // Remove any checkmarks/icons that Dawn adds
    const icons = label.querySelectorAll('svg, .icon, .checkmark');
    icons.forEach(icon => icon.remove());
  });  // <-- This closing bracket was missing!

  // Add class to fieldset for styling
  fieldset.classList.add('color-swatches-fieldset');
  fieldset.classList.add('swatch-fieldset-converted');
  
  // Remove all icons from the entire fieldset as backup
  const allIcons = fieldset.querySelectorAll('svg, .icon, .checkmark');
  allIcons.forEach(icon => icon.remove());
}


getSwatchImageForColor(optionName, colorValue) {
  //console.log('🎨 Getting swatch for:', colorValue);
  
  // First, try to get swatch from the dedicated color swatches data
  try {
    const swatchDataElement = document.getElementById('color-swatches-data');
    //console.log('📋 Swatch data element:', swatchDataElement);
    
    if (swatchDataElement) {
      const swatchData = JSON.parse(swatchDataElement.textContent);
      //console.log('✅ Parsed swatch data:', swatchData);
      //console.log('🔍 Looking for color:', colorValue, 'Found:', swatchData.swatches[colorValue]);
      
      if (swatchData.swatches && swatchData.swatches[colorValue]) {
        //console.log('✨ Returning swatch URL:', swatchData.swatches[colorValue]);
        return swatchData.swatches[colorValue];
      }
    }
  } catch (e) {
    console.warn('Could not load color swatches data:', e);
  }
  
  // Fallback to original method (searching through variants)
  if (!this.productData?.variants) return null;
  
  // Find a variant with this color value in the correct option position
  const optionIndex = this.getOptionIndex(optionName);
  if (optionIndex === -1) return null;
  
  // Look through variants to find one with this color
  for (const variant of this.productData.variants) {
    const variantOptionValue = variant[`option${optionIndex + 1}`];
    
    if (variantOptionValue === colorValue) {
      // Check for direct swatch_image property first
      if (variant.swatch_image) {
        return variant.swatch_image;
      }
      
      // Check if variant has the swatch metafield
      const metafieldKey = `${this.options.metafieldNamespace}.${this.options.metafieldKey}`;
      
      // Try different metafield access patterns
      let swatchUrl = null;
      
      // Pattern 1: variant.metafields.custom.color_swatch
      if (variant.metafields?.[this.options.metafieldNamespace]?.[this.options.metafieldKey]) {
        swatchUrl = variant.metafields[this.options.metafieldNamespace][this.options.metafieldKey];
      }
      // Pattern 2: Direct key access
      else if (variant[metafieldKey]) {
        swatchUrl = variant[metafieldKey];
      }
      // Pattern 3: Flattened metafields object
      else if (variant.metafields) {
        for (const [key, value] of Object.entries(variant.metafields)) {
          if (key.includes(this.options.metafieldKey)) {
            swatchUrl = value;
            break;
          }
        }
      }
      
      if (swatchUrl) {
        // Handle different metafield value formats
        if (typeof swatchUrl === 'string') {
          return swatchUrl;
        } else if (swatchUrl.url) {
          return swatchUrl.url;
        } else if (swatchUrl.value) {
          return swatchUrl.value;
        }
      }
    }
  }
  
  return null;
}

  getOptionIndex(optionName) {
  if (!this.productData?.options) return -1;
  
  const normalized = optionName.toLowerCase().trim();
  
  return this.productData.options.findIndex(opt => {
    if (!opt) return false; // Skip null/undefined
    const optName = typeof opt === 'string' ? opt : (opt.name || '');
    return optName.toLowerCase().trim() === normalized;
  });
}


  createSwatchElement(colorValue, swatchImageUrl, optionName) {
    //console.log('🖼️ Creating swatch element for:', colorValue, 'URL:', swatchImageUrl);
    
    const container = document.createElement('span');
    container.className = 'color-swatch-container';
    container.dataset.color = colorValue;
    container.dataset.optionName = optionName;
    
    // Create swatch visual
    const swatch = document.createElement('span');
    swatch.className = 'color-swatch';
    swatch.style.width = this.options.swatchSize;
    swatch.style.height = this.options.swatchSize;
    
    if (swatchImageUrl) {
      //console.log('✅ Setting background image:', swatchImageUrl);
      swatch.style.backgroundImage = `url(${swatchImageUrl})`;
      swatch.classList.add('has-image');
    } else {
      //console.log('❌ No swatch URL, using fallback for:', colorValue);
      // Fallback: try to use color name as background
      const fallbackColor = this.getColorFromName(colorValue);
      if (fallbackColor) {
        swatch.style.backgroundColor = fallbackColor;
        swatch.classList.add('color-fallback');
      } else {
        // Ultimate fallback: show first letter
        swatch.textContent = colorValue.charAt(0).toUpperCase();
        swatch.classList.add('text-fallback');
      }
    }
    
    // Add tooltip if enabled
    if (this.options.enableTooltip) {
      container.title = `${optionName}: ${colorValue}`;
    }
    
    container.appendChild(swatch);
    
    return container;
}

 replaceWithSwatch(label, swatchElement, input) {
  // VERY aggressively hide the radio input
  input.style.cssText = `
    position: absolute !important;
    left: -9999px !important;
    opacity: 0 !important;
    pointer-events: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    visibility: hidden !important;
  `;
  input.tabIndex = -1;
  
  // Prevent focus from causing scroll - blur immediately
  input.addEventListener('focus', (e) => {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    e.target.blur();
    window.scrollTo(scrollX, scrollY);
  }, true);
  
  // Clear label content except input
  Array.from(label.childNodes).forEach(node => {
    if (node !== input && node.nodeType === Node.TEXT_NODE) {
      node.textContent = '';
    } else if (node !== input && node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName !== 'INPUT') {
        node.style.display = 'none';
      }
    }
  });
  
  // Add swatch
  label.appendChild(swatchElement);
  label.classList.add('color-swatch-label-wrapper');
  
  // Prevent tap highlight on label
  label.style.webkitTapHighlightColor = 'transparent';
  label.style.outline = 'none';
  
  // Update selected state
  this.updateSwatchState(label, input.checked);
}

  updateSwatchState(label, isSelected) {
    if (isSelected) {
      label.classList.add('selected');
    } else {
      label.classList.remove('selected');
    }
  }

setupEventListeners() {
  // Prevent scrolling when clicking/touching labels
  const handleSwatchInteraction = (e) => {
    const swatch = e.target.closest('.color-swatch-container');
    const label = e.target.closest('label');
    
    if (swatch && label) {
      e.preventDefault();
      e.stopPropagation();
      
      // Save current scroll position
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      
      // Try to find input inside label first, then by 'for' attribute
      let input = label.querySelector('input[type="radio"]');
      if (!input && label.getAttribute('for')) {
        input = document.getElementById(label.getAttribute('for'));
      }
      
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Immediately restore scroll and keep restoring it
      window.scrollTo(scrollX, scrollY);
      requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
      setTimeout(() => window.scrollTo(scrollX, scrollY), 0);
      setTimeout(() => window.scrollTo(scrollX, scrollY), 50);
      setTimeout(() => window.scrollTo(scrollX, scrollY), 100);
      setTimeout(() => window.scrollTo(scrollX, scrollY), 200);
    }
  };
  
  // Listen for both click and touchend events
  document.addEventListener('click', handleSwatchInteraction, true);
  document.addEventListener('touchend', handleSwatchInteraction, true);
  
  // Listen for radio button changes - USE ARROW FUNCTION to preserve 'this'
  document.addEventListener('change', (e) => {
    if (e.target.type === 'radio' && (e.target.name.includes('Color') || e.target.name.includes('options'))) {
      // Save scroll position before handling variant change
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      
      // Call handleVariantChange with proper 'this' context
      this.handleVariantChange(e.target);
      
      // Restore scroll position multiple times
      requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
      setTimeout(() => window.scrollTo(scrollX, scrollY), 0);
      setTimeout(() => window.scrollTo(scrollX, scrollY), 100);
      setTimeout(() => window.scrollTo(scrollX, scrollY), 200);
    }
  });
  
  // Listen for Dawn's variant update (AFTER it renders)
  document.addEventListener('variant:change', () => {
    setTimeout(() => {
      this.convertToSwatches();
      this.updateAllColorHeadings();
    }, 0);
  });
  
  // Also listen on the product-info element directly
  const productInfo = document.querySelector('product-info');
  if (productInfo) {
    const observer = new MutationObserver(() => {
      setTimeout(() => {
        this.convertToSwatches();
        this.updateAllColorHeadings();
      }, 0);
    });
    
    observer.observe(productInfo, { 
      childList: true, 
      subtree: true 
    });
  }
}

updateColorHeading(optionName, colorValue) {
  
  // Normalize the option name
  const normalizedOption = optionName.toLowerCase();
  
  // Find all color fieldsets
  const fieldsets = document.querySelectorAll('fieldset.color-swatches-fieldset');
  
  fieldsets.forEach(fieldset => {
    const legend = fieldset.querySelector('legend');
    if (!legend) return;
    
    // Get the original legend text (before any updates)
    const originalText = legend.getAttribute('data-original-text') || legend.textContent;
    
    // Store original text if not already stored
    if (!legend.getAttribute('data-original-text')) {
      legend.setAttribute('data-original-text', originalText);
    }
    
    // Check if this legend matches this option
    if (originalText.toLowerCase().includes(normalizedOption)) {
      // Extract just the base name (e.g., "Top" or "Bottom")
      const baseName = originalText.split(' ')[0].toUpperCase();
      legend.textContent = `${baseName} COLOR: ${colorValue}`;
    }
  });
}

handleVariantChange(input) {
  console.log('🎯 handleVariantChange called with:', input.value);
  
  // Update swatch selected states
  const name = input.name;
  console.log('📝 Input name:', name);
  
  // Escape the name for use in querySelector
  const escapedName = CSS.escape(name);
  
  // Remove selected from all in this group
  document.querySelectorAll(`input[name="${escapedName}"]`).forEach(radio => {
    const label = radio.closest('label');
    if (label) {
      this.updateSwatchState(label, radio === input && radio.checked);
    }
  });
  
  // Determine which color option this is
  const isTopColor = name.toLowerCase().includes('top');
  const isBottomColor = name.toLowerCase().includes('bottom');
  
  // Update the heading
  if (isTopColor || isBottomColor) {
    const optionName = isTopColor ? 'Top Color' : 'Bottom Color';
    this.updateColorHeading(optionName, input.value);
  }
  
  // Get BOTH currently selected colors from the page
  const topColorInput = document.querySelector('input[name*="Top Color"]:checked');
  const bottomColorInput = document.querySelector('input[name*="Bottom Color"]:checked');
  
  const topColor = topColorInput ? topColorInput.value : null;
  const bottomColor = bottomColorInput ? bottomColorInput.value : null;
  
  console.log('🎨 Currently selected - Top:', topColor, 'Bottom:', bottomColor);
  
  // Update image if we have both colors
  if (this.options.enableImageSwitching && topColor && bottomColor) {
    console.log('🖼️ Updating image...');
    this.updateImageFromColors(topColor, bottomColor);
  }
}

updateImageFromColors(topColor, bottomColor) {
  // Map the color names to CDN names
  const mappedTopColor = this.mapColorToCdnName(topColor);
  const mappedBottomColor = this.mapColorToCdnName(bottomColor);
  
  // Get product title and folder
  const productTitle = this.getProductTitle();
  const productFolder = this.getProductFolderName(productTitle);
  
  // Get the correct filename prefix (handles special cases)
  const filenamePrefix = this.isComfoBackProduct(productTitle) 
    ? this.getFilenamePrefixForProduct(productTitle)
    : this.slugify(productTitle);
  
  // Construct the CDN URL
  const topSlug = this.slugify(mappedTopColor);
  const bottomSlug = this.slugify(mappedBottomColor);
  const imageUrl = `${this.options.cdnBaseUrl}/${productFolder}/${filenamePrefix}-${topSlug}-${bottomSlug}.jpg`;
  
  console.log('[CDN Images] Product title:', productTitle);
  console.log('[CDN Images] Product folder:', productFolder);
  console.log('[CDN Images] Filename prefix:', filenamePrefix);
  console.log('[CDN Images] Top color:', topColor, '→', mappedTopColor, '→', topSlug);
  console.log('[CDN Images] Bottom color:', bottomColor, '→', mappedBottomColor, '→', bottomSlug);
  console.log('[CDN Images] Loading:', imageUrl);
  
  // Swap the image
  this.swapHeroImage(imageUrl);
}

getFilenamePrefixForProduct(productTitle) {
  // Special cases that don't follow the standard pattern
  const specialCases = {
    'XT Chair': 'Comfo-Back-Swivel-XT',
    'Three Seat Glider with Console': 'Comfo-Back-Three-Seat-Glider-w-Console',
    'Three Seat Swing with Console': 'Comfo-Back-Three-Seat-Swing-w-Console',
    'Folding Adirondack Chair': 'Comfo-Back-Folding-Adirondack',
    'Upright Adirondack Chair': 'Comfo-Back-Upright-Adirondack'
    // Add other exceptions as you discover them
  };
  
  // Check if this product has a special case
  if (specialCases[productTitle]) {
    console.log('[DEBUG] Using special case prefix for:', productTitle);
    return specialCases[productTitle];
  }
  
  // Default: Comfo-Back-{Product-Title}
  const defaultPrefix = `Comfo-Back-${this.slugify(productTitle)}`;
  console.log('[DEBUG] Using default prefix for:', productTitle, '→', defaultPrefix);
  return defaultPrefix;
}

getProductFolderName(productTitle) {
  console.log('[DEBUG] getProductFolderName input:', productTitle);
  
  // Check if this product belongs to Comfo-Back collection
  if (this.isComfoBackProduct(productTitle)) {
    // Manually construct: COMFO_BACK_PRODUCT_TITLE
    const productPart = this.convertToFolderName(productTitle);
    console.log('[DEBUG] productPart after conversion:', productPart);
    const result = 'COMFO_BACK_' + productPart;
    console.log('[DEBUG] final folder name:', result);
    return result;
  }
  
  // For other collections, just convert the title
  const result = this.convertToFolderName(productTitle);
  console.log('[DEBUG] final folder name (non-Comfo):', result);
  return result;
}


isComfoBackProduct(productTitle) {
  console.log('[DEBUG] isComfoBackProduct checking:', `"${productTitle}"`);
  
  // List of Comfo-Back products
  const comfoBackProducts = [
    'Folding Adirondack Chair',
    'Bar Chair',
    'Chaise Lounge',
    'Counter Chair',
    'Dining Chair',
    'Double Swing',
    'Loveseat',
    'Rocker',
    'Swivel Bar Chair',
    'Swivel Counter Chair',
    'Swivel Rocker Dining Chair',
    'Swivel XT Chair',
    'Three Seat Glider with Console',
    'Three Seat Swing with Console',
    'Upright Adirondack Chair',
    'XT Chair'
  ];
  
  const result = comfoBackProducts.includes(productTitle);
  console.log('[DEBUG] isComfoBackProduct result:', result);
  return result;
}

getProductTitle() {
  // Try to get product title from the page
  const titleElement = document.querySelector('.product__title, h1.product-title, [itemprop="name"]');
  if (titleElement) {
    let title = titleElement.textContent
      .trim()
      .replace(/\s+/g, ' ')  // Replace multiple spaces/newlines with single space
      .replace(/\n/g, '');    // Remove any newlines
    
    // Check if title is duplicated (e.g., "Folding Adirondack Chair Folding Adirondack Chair")
    const words = title.split(' ');
    const halfLength = words.length / 2;
    
    // If the first half equals the second half, it's duplicated
    if (words.length % 2 === 0) {
      const firstHalf = words.slice(0, halfLength).join(' ');
      const secondHalf = words.slice(halfLength).join(' ');
      
      if (firstHalf === secondHalf) {
        title = firstHalf;
        console.log('[DEBUG] Detected duplicate title, using first half:', title);
      }
    }
    
    console.log('[DEBUG] Product title from page:', title);
    return title;
  }
  
  // Fallback: try to get from product JSON
  const productJson = document.querySelector('script[type="application/json"][data-product-json]');
  if (productJson) {
    try {
      const product = JSON.parse(productJson.textContent);
      let title = product.title.trim().replace(/\s+/g, ' ').replace(/\n/g, '');
      console.log('[DEBUG] Product title from JSON:', title);
      return title;
    } catch (e) {
      console.error('Failed to parse product JSON:', e);
    }
  }
  
  return '';
}
b   

convertToFolderName(title) {
  // Convert "Folding Adirondack Chair" to "FOLDING_ADIRONDACK_CHAIR"
  return title
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
}


updateAllColorHeadings() {
 
  // Find selected swatches by the .selected class on labels
  const selectedLabels = document.querySelectorAll('.product-form__input label.selected');

  
  selectedLabels.forEach(label => {
    const input = label.querySelector('input') || document.getElementById(label.getAttribute('for'));
    if (input) {
      
      // Parse the name - could be "options[Top Color]" or "Top Color-1"
      let optionName = null;
      
      // Try standard format first
      const optionMatch = input.name.match(/options\[(.+?)\]/);
      if (optionMatch) {
        optionName = optionMatch[1];
      } else {
        // Try alternate format: "Top Color-1\n" -> "Top Color"
        // Remove everything after and including the dash
        optionName = input.name.split('-')[0].trim();
      }
      
      if (optionName) {
        this.updateColorHeading(optionName, input.value);
      }
    }
  });
}


  updateVariant() {
    if (!this.productData?.variants) return;
    
    // Get all currently selected options
    const selectedValues = this.getAllSelectedOptions();
    
    // Find matching variant
    const variant = this.findVariantByOptions(selectedValues);
    
    if (variant && variant !== this.currentVariant) {
      this.currentVariant = variant;
      this.updateProductImages(variant);
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('dual-color:variant-changed', {
        detail: { variant, selectedOptions: selectedValues }
      }));
    }
  }

  getAllSelectedOptions() {
    const options = [];
    const form = document.querySelector(this.options.productFormSelector);
    
    if (!form) return options;
    
    // Get selected radio buttons
    const radios = form.querySelectorAll('input[type="radio"]:checked[name^="options"]');
    radios.forEach(radio => {
      options.push(radio.value);
    });
    
    // Get selected options from dropdowns
    const selects = form.querySelectorAll('select[name^="options"]');
    selects.forEach(select => {
      if (select.value) {
        options.push(select.value);
      }
    });
    
    return options;
  }

  findVariantByOptions(options) {
    if (!this.productData?.variants) return null;
    
    return this.productData.variants.find(variant => {
      return options.every((optionValue, index) => {
        return variant[`option${index + 1}`] === optionValue;
      });
    });
  }

  updateProductImages(variant) {
  // If CDN mode is enabled, construct URL from variant options
  if (this.options.useCdnForProductImages) {
    this.updateProductImageFromCdn(variant);
    return;
  }
  
  // Otherwise, use the original Shopify media library logic
  if (!variant.featured_media && !variant.featured_image) return;
  
  const gallery = document.querySelector('.product__media-list, [id^="GalleryViewer"]');
  if (!gallery) return;
  
  const mediaId = variant.featured_media?.id;
  if (!mediaId) return;
  
  const mediaItem = gallery.querySelector(`[data-media-id="${mediaId}"]`);
  
  if (mediaItem) {
    const slider = mediaItem.closest('slider-component');
    if (slider && slider.slideScrollPosition) {
      const slideIndex = Array.from(gallery.children).indexOf(mediaItem.closest('li'));
      slider.slideScrollPosition = slideIndex;
    } else {
      mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    
    const thumbnail = document.querySelector(`[data-target="${mediaId}"]`);
    if (thumbnail) {
      thumbnail.click();
    }
  }
}

updateProductImageFromCdn(variant) {
  if (!variant) return;
  
  console.log('[CDN Images] Variant received:', variant);
  
  // Get the top and bottom color from variant options
  const topColor = variant.option1;
  const bottomColor = variant.option2;
  
  console.log('[CDN Images] Top Color:', topColor, 'Bottom Color:', bottomColor);
  
  // If we don't have both colors, can't construct URL
  if (!topColor || !bottomColor) {
    console.warn('[CDN Images] Could not determine both colors from variant');
    return;
  }
  
  // Construct the CDN URL
  const topSlug = this.slugify(topColor);
  const bottomSlug = this.slugify(bottomColor);
  const imageUrl = `${this.options.cdnBaseUrl}/${this.options.productImagePrefix}-${topSlug}-${bottomSlug}.jpg`;
  
  console.log('[CDN Images] Loading:', imageUrl);
  
  // Find the main product image and swap it
  this.swapHeroImage(imageUrl);
}

swapHeroImage(imageUrl) {
  const selectors = [
    '.product__media img.product__image',
    '.product__modal-opener img',
    'img[data-media-id]',
    '.product-media-container img',
  ];
  
  let mainImg = null;
  for (const selector of selectors) {
    mainImg = document.querySelector(selector);
    if (mainImg) break;
  }
  
  if (!mainImg) {
    console.warn('[CDN Images] Could not find main product image element');
    return;
  }

  // Lock container height to prevent layout shift scroll
  const container = mainImg.closest('.product__media, li');
  if (container) container.style.minHeight = container.offsetHeight + 'px';

  mainImg.src = imageUrl;
  if (mainImg.hasAttribute('srcset')) mainImg.srcset = `${imageUrl} 1x`;

  const anchor = mainImg.closest('a');
  if (anchor) anchor.href = imageUrl;

  // Release height lock after image loads
  mainImg.onload = () => {
    if (container) container.style.minHeight = '';
  };

  console.log('[CDN Images] Image swapped successfully');
}


mapColorToCdnName(colorName) {
  // Map variant color names to CDN filename colors
  const colorMap = {
    'Seafoam': 'Sea-Foam',
    'Driftwood Gray': 'Driftwood-Grey',
    'Coastal Gray': 'Dark-Grey',
    'Sage Green': 'Sage'
    // Add more mappings as needed
  };
  
  return colorMap[colorName] || colorName;
}

slugify(str) {
  return str
    .toString()
    .trim()
    .replace(/\s+/g, '-')           // spaces to hyphens
    .replace(/[^\w\-]+/g, '')       // remove non-word chars
    .replace(/\-\-+/g, '-')         // collapse multiple hyphens
    .replace(/^-+/, '')             // trim start hyphens
    .replace(/-+$/, '');            // trim end hyphens
}

  getColorFromName(colorName) {
    const colorMap = {
      'white': '#FFFFFF',
      'black': '#000000',
      'red': '#FF0000',
      'blue': '#0000FF',
      'green': '#008000',
      'yellow': '#FFFF00',
      'orange': '#FFA500',
      'purple': '#800080',
      'pink': '#FFC0CB',
      'brown': '#A52A2A',
      'gray': '#808080',
      'grey': '#808080',
      'navy': '#000080',
      'beige': '#F5F5DC',
      'tan': '#D2B48C',
      'silver': '#C0C0C0',
      'gold': '#FFD700'
    };
    
    return colorMap[colorName.toLowerCase()] || null;
  }
}

// Auto-initialize when DOM is ready
if (typeof window !== 'undefined') {
  const initSwatches = () => {
    // Check if we're on a product page
    if (document.querySelector('product-form, [data-section-type="product"]')) {
      window.dualColorSwatches = new DualColorVariantSwatches({
  topColorOption: 'Top Color',
  bottomColorOption: 'Bottom Color',
  metafieldNamespace: 'custom',
  metafieldKey: 'color_swatch'
});

      console.log('✓ Dual Color Swatches initialized');
    }
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwatches);
  } else {
    initSwatches();
  }
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DualColorVariantSwatches;
}
